"use client";

import {
  CartesianGrid,
  Cell,
  ErrorBar,
  Label,
  Legend as RechartsLegend,
  ScatterChart as RechartsScatterChart,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { AxisDomain } from "recharts/types/util/types";

import React from "react";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";

import { useOnWindowResize } from "@/hooks/use-on-window-resize";

import {
  AvailableChartColors,
  AvailableChartColorsKeys,
  constructCategoryColors,
  getColorClassName,
  getYAxisDomain,
} from "@/lib/chart-utils";
import { cx } from "@/lib/tailwind-utils";

//#region Legend

interface LegendItemProps {
  name: string;
  color: AvailableChartColorsKeys;
  onClick?: (name: string, color: AvailableChartColorsKeys) => void;
  activeLegend?: string;
}

const LegendItem = ({
  name,
  color,
  onClick,
  activeLegend,
}: LegendItemProps) => {
  const hasOnValueChange = !!onClick;
  return (
    <li
      className={cx(
        "group inline-flex flex-nowrap items-center gap-1.5 whitespace-nowrap rounded px-2 py-1 transition",
        hasOnValueChange ? "cursor-pointer hover:bg-hover" : "cursor-default",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(name, color);
      }}
    >
      <span
        className={cx(
          "h-2.5 w-2.5 shrink-0 rounded-full",
          getColorClassName(color, "bg"),
          activeLegend && activeLegend !== name ? "opacity-40" : "opacity-100",
        )}
        aria-hidden={true}
      />
      <p
        className={cx(
          "truncate whitespace-nowrap text-caption",
          "text-foreground-secondary",
          hasOnValueChange && "group-hover:text-foreground",
          activeLegend && activeLegend !== name ? "opacity-40" : "opacity-100",
        )}
      >
        {name}
      </p>
    </li>
  );
};

interface ScrollButtonProps {
  icon: React.ElementType<{ className?: string }>;
  onClick?: () => void;
  disabled?: boolean;
}

const ScrollButton = ({ icon, onClick, disabled }: ScrollButtonProps) => {
  const Icon = icon;
  const [isPressed, setIsPressed] = React.useState(false);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (isPressed) {
      intervalRef.current = setInterval(() => {
        onClick?.();
      }, 300);
    } else {
      clearInterval(intervalRef.current as NodeJS.Timeout);
    }
    return () => clearInterval(intervalRef.current as NodeJS.Timeout);
  }, [isPressed, onClick]);

  React.useEffect(() => {
    if (disabled) {
      clearInterval(intervalRef.current as NodeJS.Timeout);
      setIsPressed(false);
    }
  }, [disabled]);

  return (
    <button
      type="button"
      className={cx(
        "group inline-flex size-5 items-center truncate rounded transition",
        disabled
          ? "cursor-not-allowed text-foreground-muted"
          : "cursor-pointer text-foreground-secondary hover:bg-hover hover:text-foreground",
      )}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        setIsPressed(true);
      }}
      onMouseUp={(e) => {
        e.stopPropagation();
        setIsPressed(false);
      }}
    >
      <Icon className="size-full" aria-hidden="true" />
    </button>
  );
};

interface LegendProps extends React.OlHTMLAttributes<HTMLOListElement> {
  categories: string[];
  colors?: AvailableChartColorsKeys[];
  onClickLegendItem?: (category: string, color: string) => void;
  activeLegend?: string;
  enableLegendSlider?: boolean;
}

type HasScrollProps = {
  left: boolean;
  right: boolean;
};

const Legend = React.forwardRef<HTMLOListElement, LegendProps>((props, ref) => {
  const {
    categories,
    colors = AvailableChartColors,
    className,
    onClickLegendItem,
    activeLegend,
    enableLegendSlider = false,
    ...other
  } = props;
  const scrollableRef = React.useRef<HTMLInputElement>(null);
  const scrollButtonsRef = React.useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = React.useState<HasScrollProps | null>(null);
  const [isKeyDowned, setIsKeyDowned] = React.useState<string | null>(null);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const checkScroll = React.useCallback(() => {
    const scrollable = scrollableRef?.current;
    if (!scrollable) return;
    const hasLeftScroll = scrollable.scrollLeft > 0;
    const hasRightScroll =
      scrollable.scrollWidth - scrollable.clientWidth > scrollable.scrollLeft;
    setHasScroll({ left: hasLeftScroll, right: hasRightScroll });
  }, [setHasScroll]);

  const scrollToTest = React.useCallback(
    (direction: "left" | "right") => {
      const element = scrollableRef?.current;
      const scrollButtons = scrollButtonsRef?.current;
      const scrollButtonsWith = scrollButtons?.clientWidth ?? 0;
      const width = element?.clientWidth ?? 0;
      if (element && enableLegendSlider) {
        element.scrollTo({
          left:
            direction === "left"
              ? element.scrollLeft - width + scrollButtonsWith
              : element.scrollLeft + width - scrollButtonsWith,
          behavior: "smooth",
        });
        setTimeout(() => {
          checkScroll();
        }, 400);
      }
    },
    [enableLegendSlider, checkScroll],
  );

  React.useEffect(() => {
    const keyDownHandler = (key: string) => {
      if (key === "ArrowLeft") {
        scrollToTest("left");
      } else if (key === "ArrowRight") {
        scrollToTest("right");
      }
    };
    if (isKeyDowned) {
      keyDownHandler(isKeyDowned);
      intervalRef.current = setInterval(() => {
        keyDownHandler(isKeyDowned);
      }, 300);
    } else {
      clearInterval(intervalRef.current as NodeJS.Timeout);
    }
    return () => clearInterval(intervalRef.current as NodeJS.Timeout);
  }, [isKeyDowned, scrollToTest]);

  const keyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      setIsKeyDowned(e.key);
    }
  };
  const keyUp = (e: KeyboardEvent) => {
    e.stopPropagation();
    setIsKeyDowned(null);
  };

  React.useEffect(() => {
    const scrollable = scrollableRef?.current;
    if (enableLegendSlider) {
      checkScroll();
      scrollable?.addEventListener("keydown", keyDown);
      scrollable?.addEventListener("keyup", keyUp);
    }
    return () => {
      scrollable?.removeEventListener("keydown", keyDown);
      scrollable?.removeEventListener("keyup", keyUp);
    };
  }, [checkScroll, enableLegendSlider]);

  return (
    <ol
      ref={ref}
      className={cx("relative overflow-hidden", className)}
      {...other}
    >
      <div
        ref={scrollableRef}
        tabIndex={0}
        className={cx(
          "flex h-full",
          enableLegendSlider
            ? hasScroll?.right || hasScroll?.left
              ? "snap-mandatory items-center overflow-auto pl-4 pr-12 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              : ""
            : "flex-wrap",
        )}
      >
        {categories.map((category, index) => (
          <LegendItem
            key={`item-${index}`}
            name={category}
            color={colors[index] as AvailableChartColorsKeys}
            onClick={onClickLegendItem}
            activeLegend={activeLegend}
          />
        ))}
      </div>
      {enableLegendSlider && (hasScroll?.right || hasScroll?.left) ? (
        <div className="absolute bottom-0 right-0 top-0 flex h-full items-center justify-center pr-1">
          <ScrollButton
            icon={RiArrowLeftSLine}
            onClick={() => {
              setIsKeyDowned(null);
              scrollToTest("left");
            }}
            disabled={!hasScroll?.left}
          />
          <ScrollButton
            icon={RiArrowRightSLine}
            onClick={() => {
              setIsKeyDowned(null);
              scrollToTest("right");
            }}
            disabled={!hasScroll?.right}
          />
        </div>
      ) : null}
    </ol>
  );
});

Legend.displayName = "Legend";

const ChartLegend = (
  { payload }: any,
  categoryColors: Map<string, AvailableChartColorsKeys>,
  setLegendHeight: React.Dispatch<React.SetStateAction<number>>,
  activeLegend: string | undefined,
  onClick?: (category: string, color: string) => void,
  enableLegendSlider?: boolean,
  legendPosition?: "left" | "center" | "right",
  yAxisWidth?: number,
) => {
  const legendRef = React.useRef<HTMLDivElement>(null);

  useOnWindowResize(() => {
    const calculateHeight = (height: number | undefined) =>
      height ? Number(height) + 15 : 60;
    setLegendHeight(calculateHeight(legendRef.current?.clientHeight));
  });

  const legendPayload = payload.filter((item: any) => item.type !== "none");
  const paddingLeft =
    legendPosition === "left" && yAxisWidth ? yAxisWidth - 8 : 0;

  return (
    <div
      ref={legendRef}
      style={{ paddingLeft }}
      className={cx(
        "flex items-center",
        { "justify-center": legendPosition === "center" },
        { "justify-start": legendPosition === "left" },
        { "justify-end": legendPosition === "right" },
      )}
    >
      <Legend
        categories={legendPayload.map((entry: any) => entry.value)}
        colors={legendPayload.map((entry: any) =>
          categoryColors.get(entry.value),
        )}
        onClickLegendItem={onClick}
        activeLegend={activeLegend}
        enableLegendSlider={enableLegendSlider}
      />
    </div>
  );
};

//#region Tooltip

type TooltipProps = Pick<ChartTooltipProps, "active" | "payload" | "label">;

type PayloadItem = {
  category: string;
  value: number;
  index: string;
  color: AvailableChartColorsKeys;
  type?: string;
  payload: any;
};

interface ChartTooltipProps {
  active: boolean | undefined;
  payload: PayloadItem[];
  label: string;
  xLabel?: string;
  yLabel?: string;
  valueFormatter: (value: number) => string;
  xValueFormatter?: (value: number) => string;
  yValueFormatter?: (value: number) => string;
}

const ChartTooltip = ({
  active,
  payload,
  xLabel,
  yLabel,
  valueFormatter,
  xValueFormatter,
  yValueFormatter,
}: ChartTooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    if (!data) return null;
    const raw = data.payload;
    return (
      <div
        className={cx(
          "rounded-md border bg-background/80 text-body shadow-md backdrop-blur-2xl",
          "border-border",
        )}
      >
        {data.category && (
          <div className="border-b border-inherit px-4 py-2">
            <div className="flex items-center gap-1.5">
              <span
                className={cx(
                  "h-2.5 w-2.5 shrink-0 rounded-full",
                  getColorClassName(data.color, "bg"),
                )}
                aria-hidden
              />
              <p className="font-medium text-foreground">{data.category}</p>
            </div>
          </div>
        )}
        <div className="space-y-1 px-4 py-2">
          <div className="flex items-center justify-between space-x-8">
            <p className="whitespace-nowrap text-foreground-secondary">
              {xLabel ?? "X"}
            </p>
            <p className="whitespace-nowrap font-medium tabular-nums text-foreground">
              {(xValueFormatter ?? valueFormatter)(raw.x)}
            </p>
          </div>
          <div className="flex items-center justify-between space-x-8">
            <p className="whitespace-nowrap text-foreground-secondary">
              {yLabel ?? "Y"}
            </p>
            <p className="whitespace-nowrap font-medium tabular-nums text-foreground">
              {(yValueFormatter ?? valueFormatter)(raw.y)}
            </p>
          </div>
          {raw.errorX != null && (
            <div className="flex items-center justify-between space-x-8">
              <p className="whitespace-nowrap text-foreground-secondary">
                {xLabel ? `${xLabel} ±` : "X ±"}
              </p>
              <p className="whitespace-nowrap font-medium tabular-nums text-foreground-muted">
                {valueFormatter(
                  Array.isArray(raw.errorX)
                    ? (raw.errorX[0] + raw.errorX[1]) / 2
                    : raw.errorX,
                )}
              </p>
            </div>
          )}
          {raw.errorY != null && (
            <div className="flex items-center justify-between space-x-8">
              <p className="whitespace-nowrap text-foreground-secondary">
                {yLabel ? `${yLabel} ±` : "Y ±"}
              </p>
              <p className="whitespace-nowrap font-medium tabular-nums text-foreground-muted">
                {valueFormatter(
                  Array.isArray(raw.errorY)
                    ? (raw.errorY[0] + raw.errorY[1]) / 2
                    : raw.errorY,
                )}
              </p>
            </div>
          )}
          {raw.z != null && (
            <div className="flex items-center justify-between space-x-8">
              <p className="whitespace-nowrap text-foreground-secondary">
                Size
              </p>
              <p className="whitespace-nowrap font-medium tabular-nums text-foreground">
                {valueFormatter(raw.z)}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

//#region ScatterChart

/** Data point for scatter chart */
export interface ScatterDataPoint {
  x: number;
  y: number;
  /** Optional size dimension (bubble chart) */
  z?: number;
  /** Error bar for x-axis: single value for symmetric, [lower, upper] for asymmetric */
  errorX?: number | [number, number];
  /** Error bar for y-axis: single value for symmetric, [lower, upper] for asymmetric */
  errorY?: number | [number, number];
  /** Category/series label for grouping */
  category?: string;
  /** Optional label for the data point */
  label?: string;
  [key: string]: unknown;
}

type ScatterChartEventProps =
  | {
      eventType: "dot" | "category";
      categoryClicked: string;
      [key: string]: unknown;
    }
  | null
  | undefined;

interface ScatterChartProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Data grouped by category: { [categoryName]: ScatterDataPoint[] } or flat array */
  data: Record<string, ScatterDataPoint[]> | ScatterDataPoint[];
  colors?: AvailableChartColorsKeys[];
  valueFormatter?: (value: number) => string;
  /** Override formatter for X axis only (e.g. when X is not currency but Y is) */
  xValueFormatter?: (value: number) => string;
  /** Override formatter for Y axis only */
  yValueFormatter?: (value: number) => string;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showGridLines?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  showDensity?: boolean;
  yAxisWidth?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  autoMinValue?: boolean;
  minXValue?: number;
  maxXValue?: number;
  minYValue?: number;
  maxYValue?: number;
  allowDecimals?: boolean;
  dotSize?: number;
  sizeRange?: [number, number];
  onValueChange?: (value: ScatterChartEventProps) => void;
  enableLegendSlider?: boolean;
  legendPosition?: "left" | "center" | "right";
  customTooltip?: React.ComponentType<TooltipProps>;
}

/**
 * Compute density-based opacity for each point.
 * Points in denser areas get higher opacity so clusters stand out.
 */
function computeDensityOpacities(
  points: ScatterDataPoint[],
  bandwidth?: number,
): number[] {
  if (points.length <= 1) return points.map(() => 1);

  // Auto-compute bandwidth from data range
  const xVals = points.map((p) => p.x);
  const yVals = points.map((p) => p.y);
  const xRange = Math.max(...xVals) - Math.min(...xVals) || 1;
  const yRange = Math.max(...yVals) - Math.min(...yVals) || 1;
  const bw = bandwidth ?? Math.min(xRange, yRange) * 0.15;
  const bwSq = bw * bw;

  // KDE: count neighbors within bandwidth for each point
  const densities = points.map((p, i) => {
    let count = 0;
    for (let j = 0; j < points.length; j++) {
      if (i === j) continue;
      const dx = (p.x - points[j].x) / xRange;
      const dy = (p.y - points[j].y) / yRange;
      const distSq = (dx * dx + dy * dy) * xRange * yRange;
      if (distSq < bwSq * 4) {
        count += Math.exp(-distSq / (2 * bwSq));
      }
    }
    return count;
  });

  const maxDensity = Math.max(...densities);
  if (maxDensity === 0) return points.map(() => 0.7);

  // Map density to opacity: 0.3 (sparse) to 1.0 (dense)
  return densities.map((d) => 0.3 + 0.7 * (d / maxDensity));
}

const ScatterChart = React.forwardRef<HTMLDivElement, ScatterChartProps>(
  (props, ref) => {
    const {
      data,
      colors = AvailableChartColors,
      valueFormatter = (value: number) => value.toLocaleString(),
      xValueFormatter,
      yValueFormatter,
      showXAxis = true,
      showYAxis = true,
      showGridLines = true,
      showTooltip = true,
      showLegend = true,
      showDensity = false,
      yAxisWidth = 56,
      xAxisLabel,
      yAxisLabel,
      autoMinValue = false,
      minXValue,
      maxXValue,
      minYValue,
      maxYValue,
      allowDecimals = true,
      sizeRange,
      className,
      onValueChange,
      enableLegendSlider = false,
      legendPosition = "right",
      customTooltip,
      ...other
    } = props;

    const CustomTooltip = customTooltip;
    const [legendHeight, setLegendHeight] = React.useState(60);
    const [activeLegend, setActiveLegend] = React.useState<string | undefined>(
      undefined,
    );

    // Normalize data into { categoryName: points[] } format
    const categorizedData: Record<string, ScatterDataPoint[]> =
      React.useMemo(() => {
        if (Array.isArray(data)) {
          // Group by category field, or use "Data" as default
          const grouped: Record<string, ScatterDataPoint[]> = {};
          for (const point of data) {
            const cat = point.category ?? "Data";
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(point);
          }
          return grouped;
        }
        return data;
      }, [data]);

    const categories = Object.keys(categorizedData);
    const categoryColors = constructCategoryColors(categories, colors);
    const hasOnValueChange = !!onValueChange;

    // Precompute density opacities per category
    const densityOpacities = React.useMemo(() => {
      if (!showDensity) return {};
      const result: Record<string, number[]> = {};
      for (const [cat, points] of Object.entries(categorizedData)) {
        result[cat] = computeDensityOpacities(points);
      }
      return result;
    }, [categorizedData, showDensity]);

    // Determine if any data has error bars
    const hasErrorBars = React.useMemo(() => {
      for (const points of Object.values(categorizedData)) {
        for (const p of points) {
          if (p.errorX != null || p.errorY != null) return true;
        }
      }
      return false;
    }, [categorizedData]);

    // Determine if data uses z dimension (bubble chart)
    const hasZDimension = React.useMemo(() => {
      for (const points of Object.values(categorizedData)) {
        for (const p of points) {
          if (p.z != null) return true;
        }
      }
      return false;
    }, [categorizedData]);

    const xDomain = getYAxisDomain(autoMinValue, minXValue, maxXValue);
    const yDomain = getYAxisDomain(autoMinValue, minYValue, maxYValue);

    function onCategoryClick(dataKey: string) {
      if (!hasOnValueChange) return;
      if (dataKey === activeLegend) {
        setActiveLegend(undefined);
        onValueChange?.(null);
      } else {
        setActiveLegend(dataKey);
        onValueChange?.({
          eventType: "category",
          categoryClicked: dataKey,
        });
      }
    }

    return (
      <div
        ref={ref}
        className={cx("h-80 w-full", className)}
        tremor-id="tremor-raw"
        {...other}
      >
        <ResponsiveContainer>
          <RechartsScatterChart
            margin={{
              bottom: xAxisLabel ? 30 : 5,
              left: yAxisLabel ? 20 : 5,
              right: 10,
              top: yAxisLabel ? 15 : 5,
            }}
            onClick={
              hasOnValueChange && activeLegend
                ? () => {
                    setActiveLegend(undefined);
                    onValueChange?.(null);
                  }
                : undefined
            }
          >
            {showGridLines && (
              <CartesianGrid
                className="stroke-border stroke-1"
                strokeDasharray="3 3"
              />
            )}
            <XAxis
              type="number"
              dataKey="x"
              name={xAxisLabel ?? "X"}
              hide={!showXAxis}
              domain={xDomain as AxisDomain}
              tick={{
                className: "text-caption fill-muted-foreground",
                transform: "translate(0, 6)",
              }}
              fill=""
              stroke=""
              tickLine={false}
              axisLine={false}
              allowDecimals={allowDecimals}
              tickFormatter={(v) => (xValueFormatter ?? valueFormatter)(v)}
            >
              {xAxisLabel && (
                <Label
                  position="insideBottom"
                  offset={-20}
                  className="fill-foreground text-body-strong"
                >
                  {xAxisLabel}
                </Label>
              )}
            </XAxis>
            <YAxis
              type="number"
              dataKey="y"
              name={yAxisLabel ?? "Y"}
              width={yAxisWidth}
              hide={!showYAxis}
              domain={yDomain as AxisDomain}
              tick={{
                className: "text-caption fill-muted-foreground",
                transform: "translate(-3, 0)",
              }}
              fill=""
              stroke=""
              tickLine={false}
              axisLine={false}
              allowDecimals={allowDecimals}
              tickFormatter={(v) => (yValueFormatter ?? valueFormatter)(v)}
            >
              {yAxisLabel && (
                <Label
                  position="insideLeft"
                  style={{ textAnchor: "middle" }}
                  angle={-90}
                  offset={-15}
                  className="fill-foreground text-body-strong"
                >
                  {yAxisLabel}
                </Label>
              )}
            </YAxis>
            {hasZDimension && (
              <ZAxis type="number" dataKey="z" range={sizeRange ?? [40, 400]} />
            )}
            <Tooltip
              wrapperStyle={{ outline: "none", zIndex: 10 }}
              isAnimationActive={true}
              animationDuration={100}
              cursor={{ strokeDasharray: "3 3" }}
              content={({ active, payload }) => {
                if (!showTooltip || !active || !payload?.length) return null;

                const item = payload[0] as any;
                const raw = item?.payload;
                if (!raw) return null;

                // Find category from the Scatter's name
                const categoryName = item?.name ?? "Data";
                const color = categoryColors.get(categoryName) ?? "blue";

                const cleanPayload: PayloadItem[] = [
                  {
                    category: categoryName,
                    value: raw.y,
                    index: String(raw.x),
                    color: color as AvailableChartColorsKeys,
                    payload: raw,
                  },
                ];

                if (CustomTooltip) {
                  return (
                    <CustomTooltip
                      active={active}
                      payload={cleanPayload}
                      label={String(raw.x)}
                    />
                  );
                }

                return (
                  <ChartTooltip
                    active={active}
                    payload={cleanPayload}
                    label={String(raw.x)}
                    xLabel={xAxisLabel}
                    yLabel={yAxisLabel}
                    valueFormatter={valueFormatter}
                    xValueFormatter={xValueFormatter}
                    yValueFormatter={yValueFormatter}
                  />
                );
              }}
            />
            {showLegend && categories.length > 1 && (
              <RechartsLegend
                verticalAlign="top"
                height={legendHeight}
                content={({ payload }) =>
                  ChartLegend(
                    { payload },
                    categoryColors,
                    setLegendHeight,
                    activeLegend,
                    hasOnValueChange
                      ? (clickedLegendItem: string) =>
                          onCategoryClick(clickedLegendItem)
                      : undefined,
                    enableLegendSlider,
                    legendPosition,
                    yAxisWidth,
                  )
                }
              />
            )}
            {categories.map((category) => {
              const points = categorizedData[category] ?? [];
              const color = categoryColors.get(category) ?? "blue";
              const isActive = !activeLegend || activeLegend === category;
              const opacities = densityOpacities[category];

              return (
                <Scatter
                  key={category}
                  name={category}
                  data={points}
                  fill=""
                  isAnimationActive={false}
                  onClick={
                    hasOnValueChange
                      ? (_data: any, _index: any, event: any) => {
                          event?.stopPropagation?.();
                          onValueChange?.({
                            eventType: "dot",
                            categoryClicked: category,
                            ..._data,
                          });
                        }
                      : undefined
                  }
                >
                  {hasErrorBars && (
                    <>
                      <ErrorBar
                        dataKey="errorX"
                        direction="x"
                        width={4}
                        className={cx(
                          getColorClassName(
                            color as AvailableChartColorsKeys,
                            "stroke",
                          ),
                        )}
                        strokeWidth={1.5}
                        opacity={isActive ? 0.6 : 0.15}
                      />
                      <ErrorBar
                        dataKey="errorY"
                        direction="y"
                        width={4}
                        className={cx(
                          getColorClassName(
                            color as AvailableChartColorsKeys,
                            "stroke",
                          ),
                        )}
                        strokeWidth={1.5}
                        opacity={isActive ? 0.6 : 0.15}
                      />
                    </>
                  )}
                  {points.map((_, pointIdx) => {
                    const opacity =
                      showDensity && opacities
                        ? (opacities[pointIdx] ?? 0.7)
                        : isActive
                          ? 0.8
                          : 0.2;
                    return (
                      <Cell
                        key={pointIdx}
                        className={cx(
                          getColorClassName(
                            color as AvailableChartColorsKeys,
                            "fill",
                          ),
                          hasOnValueChange ? "cursor-pointer" : "",
                        )}
                        opacity={opacity}
                        strokeWidth={1}
                        stroke="white"
                      />
                    );
                  })}
                </Scatter>
              );
            })}
          </RechartsScatterChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

ScatterChart.displayName = "ScatterChart";

export {
  ScatterChart,
  type ScatterChartEventProps,
  type ScatterChartProps,
  type TooltipProps as ScatterTooltipProps,
};
